import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices";


export const fetchItems = createAsyncThunk("items/fetchItems", async (params = {}, thunkAPI) => {
  try {
    const {
      page = 1,
      limit = 10,
      search = '',
      filters = {},
      sort = {}
    } = params;

    // Build query string
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) queryParams.append('search', search);
    if (sort.key) {
      queryParams.append('sortBy', sort.key);
      queryParams.append('sortOrder', sort.direction || 'asc');
    }

    // Add filter params
    Object.keys(filters).forEach(key => {
      if (filters[key]) queryParams.append(key, filters[key]);
    });

    const response = await apiServices.get(`items?${queryParams.toString()}`);
    return response.data; // Return fetched items (or paginated response)
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});
export const fetchItemById = createAsyncThunk("items/fetchItemById", async (id, thunkAPI) => {
  try {
    const response = await apiServices.get(`items/${id}`);
    console.log("items called to sho details ", response.data)
    return response.data; // Return the fetched item
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const createItem = createAsyncThunk(
  "items_state/createItem",
  async (data) => {
    const response = await apiServices.post("items", data);
    // console.log(response.data.data);
    return response.data.data;
  }
);
export const updateItem = createAsyncThunk(
  "items_state/updateItem",
  async (data) => {
    const response = await apiServices.put("items/" + data._id, data);
    // console.log(response.data);
    return response.data;
  }
);

export const createDefaultVariant = createAsyncThunk(
  "items_state/createDefaultVariant",
  async (data) => {
    // data should contain { itemId: "..." }
    const response = await apiServices.post("variants/default", data);
    return response.data;
  }
);

export const deleteItem = createAsyncThunk("items/deleteItem", async (id, thunkAPI) => {
  try {
    await apiServices.delete(`items/${id}`);
    return id; // Return the deleted item's ID
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});




const initialState = {
  items: [], // List of all items (or current page items)
  selectedItem: null, // Details of the selected item
  loading: false, // Loading state for API calls
  error: null, // Error message
  edititems: {},
  itemsUpdated: false,
  itemsAdded: false,
  // Pagination metadata
  total: 0,
  currentPage: 1,
  totalPages: 1,
};

export const fetchItemAnalytics = createAsyncThunk("items/fetchItemAnalytics", async (id, thunkAPI) => {
  try {
    const response = await apiServices.get(`items/${id}/analytics`);
    return response.data.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

let itemSlice = createSlice({
  name: "items_state",
  initialState: {
    ...initialState,
    itemAnalytics: null,
  },
  reducers: {
    clearSelectedItem: (state) => {
      state.selectedItem = null;
      state.itemAnalytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Items
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both paginated and non-paginated responses
        if (action.payload && typeof action.payload === 'object' && 'data' in action.payload) {
          // Paginated response: { data: [...], total, page, totalPages }
          state.items = action.payload.data || action.payload;
          state.total = action.payload.total || action.payload.data?.length || 0;
          state.currentPage = action.payload.page || 1;
          state.totalPages = action.payload.totalPages || 1;
        } else {
          // Non-paginated response (backward compatibility)
          state.items = action.payload;
          state.total = action.payload.length;
          state.currentPage = 1;
          state.totalPages = 1;
        }
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Single Item
      .addCase(fetchItemById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedItem = action.payload;
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Analytics
      .addCase(fetchItemAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchItemAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.itemAnalytics = action.payload;
      })
      .addCase(fetchItemAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Item
      .addCase(createItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Item
      .addCase(updateItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.loading = false;
        //console.log("update item payload ", action.payload)
        const index = state.items.findIndex((item) => item._id === action.payload._id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Item
      .addCase(deleteItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedItem } = itemSlice.actions;
export default itemSlice.reducer;
